import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import tracksService from '../services/tracksService';
import classesService from '../services/classesService';
import schoolsService from '../services/schoolsService';
import studentsService from '../services/studentsService';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash, GraduationCap } from '@phosphor-icons/react';
import './TracksPage.css';

const initialTrack = { name: '', academic_term_id: '', is_active: true };

function TracksPage() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [terms, setTerms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState(initialTrack);
  const [saving, setSaving] = useState(false);

  const [selectedTrackId, setSelectedTrackId] = useState('');
  const [trackSubjects, setTrackSubjects] = useState([]);
  const [trackStudents, setTrackStudents] = useState([]);
  const [addSubjectId, setAddSubjectId] = useState('');
  const [enrollStudentId, setEnrollStudentId] = useState('');
  const [enrollTermId, setEnrollTermId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [trackData, termData, subjectData, studentData] = await Promise.all([
        tracksService.list(),
        user?.school_id ? schoolsService.listTerms(user.school_id) : Promise.resolve([]),
        classesService.listAllSubjects(),
        studentsService.listStudents({ limit: 100 }),
      ]);
      setTracks(trackData || []);
      setTerms(termData || []);
      setSubjects(Array.isArray(subjectData) ? subjectData : []);
      setStudents(studentData?.items || []);
    } catch (error) {
      toast.error(error.message || 'Unable to load tracks');
    } finally {
      setLoading(false);
    }
  }, [user?.school_id]);

  useEffect(() => { load(); }, [load]);

  const termLabel = id => { const t = terms.find(x => x.id === id); return t ? `${t.term} - ${t.academic_year}` : 'Any term'; };
  const subjectName = id => subjects.find(s => s.id === id)?.name || id;
  const studentName = id => { const s = students.find(x => x.id === id); return s ? s.full_name : id; };

  const handleCreateTrack = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      await tracksService.create({ ...track, academic_term_id: track.academic_term_id || null });
      toast.success('Track created');
      setTrack(initialTrack);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to create track');
    } finally {
      setSaving(false);
    }
  };

  const loadTrackDetail = useCallback(async trackId => {
    if (!trackId) { setTrackSubjects([]); setTrackStudents([]); return; }
    try {
      const [subjectLinks, studentLinks] = await Promise.all([
        tracksService.trackSubjects(trackId),
        tracksService.trackStudents(trackId),
      ]);
      setTrackSubjects(subjectLinks || []);
      setTrackStudents(studentLinks || []);
    } catch (error) {
      toast.error(error.message || 'Unable to load track detail');
    }
  }, []);

  useEffect(() => { loadTrackDetail(selectedTrackId); }, [selectedTrackId, loadTrackDetail]);

  const handleAddSubject = async () => {
    if (!addSubjectId) return;
    try {
      await tracksService.addSubject(selectedTrackId, addSubjectId);
      setAddSubjectId('');
      await loadTrackDetail(selectedTrackId);
      await load();
      toast.success('Subject added to track');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to add subject');
    }
  };

  const handleRemoveSubject = async subjectId => {
    try {
      await tracksService.removeSubject(selectedTrackId, subjectId);
      await loadTrackDetail(selectedTrackId);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to remove subject');
    }
  };

  const handleEnrollStudent = async () => {
    if (!enrollStudentId || !enrollTermId) {
      toast.error('Select a student and term');
      return;
    }
    try {
      await tracksService.enrollStudent(selectedTrackId, enrollStudentId, enrollTermId);
      setEnrollStudentId('');
      await loadTrackDetail(selectedTrackId);
      await load();
      toast.success('Student enrolled in track');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to enroll student');
    }
  };

  const handleRemoveStudentTrack = async id => {
    try {
      await tracksService.removeStudentTrack(id);
      await loadTrackDetail(selectedTrackId);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to remove enrollment');
    }
  };

  const selectedTrack = tracks.find(t => t.id === selectedTrackId);
  const unassignedSubjects = subjects.filter(s => !trackSubjects.some(ts => ts.subject_id === s.id));

  return (
    <Layout>
      <div className="page tracks-page">
        <div className="page-header">
          <div>
            <h1>Tracks</h1>
            <p>Elective / subject-track pathways students can be enrolled into per term.</p>
          </div>
        </div>

        <div className="tracks-grid">
          <form className="tracks-panel" onSubmit={handleCreateTrack}>
            <h2>Create track</h2>
            <div className="form-group">
              <label>Name</label>
              <input required value={track.name} onChange={e => setTrack({ ...track, name: e.target.value })} placeholder="e.g. Science Track" disabled={saving} />
            </div>
            <div className="form-group">
              <label>Term (optional — leave blank for every term)</label>
              <select value={track.academic_term_id} onChange={e => setTrack({ ...track, academic_term_id: e.target.value })} disabled={saving}>
                <option value="">Any term</option>
                {terms.map(t => <option key={t.id} value={t.id}>{t.term} - {t.academic_year}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              <Plus size={16} /> {saving ? 'Creating...' : 'Create track'}
            </button>
          </form>

          <div className="tracks-panel tracks-list">
            <h2>Tracks <span>{tracks.length}</span></h2>
            {loading ? <p className="muted">Loading...</p> : tracks.length ? (
              <div className="tracks-items">
                {tracks.map(item => (
                  <button
                    key={item.id}
                    className={`track-item ${selectedTrackId === item.id ? 'active' : ''}`}
                    onClick={() => setSelectedTrackId(item.id)}
                  >
                    <GraduationCap size={18} />
                    <div>
                      <strong>{item.name}</strong>
                      <small>{termLabel(item.academic_term_id)} · {item.subject_count} subjects · {item.student_count} students</small>
                    </div>
                  </button>
                ))}
              </div>
            ) : <p className="muted">No tracks created yet.</p>}
          </div>
        </div>

        {selectedTrack && (
          <div className="tracks-grid">
            <div className="tracks-panel">
              <h2>Subjects in "{selectedTrack.name}"</h2>
              <div className="form-row">
                <select value={addSubjectId} onChange={e => setAddSubjectId(e.target.value)}>
                  <option value="">Select subject to add</option>
                  {unassignedSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button className="btn btn-secondary" onClick={handleAddSubject} disabled={!addSubjectId}>Add</button>
              </div>
              <div className="tracks-list-simple">
                {trackSubjects.map(ts => (
                  <div key={ts.id} className="tracks-list-row">
                    <span>{subjectName(ts.subject_id)}</span>
                    <button className="btn-icon" onClick={() => handleRemoveSubject(ts.subject_id)} title="Remove subject">
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
                {!trackSubjects.length && <p className="muted">No subjects assigned to this track yet.</p>}
              </div>
            </div>

            <div className="tracks-panel">
              <h2>Students in "{selectedTrack.name}"</h2>
              <div className="form-row">
                <select value={enrollStudentId} onChange={e => setEnrollStudentId(e.target.value)}>
                  <option value="">Select student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
                <select value={enrollTermId} onChange={e => setEnrollTermId(e.target.value)}>
                  <option value="">Select term</option>
                  {terms.map(t => <option key={t.id} value={t.id}>{t.term} - {t.academic_year}</option>)}
                </select>
                <button className="btn btn-secondary" onClick={handleEnrollStudent} disabled={!enrollStudentId || !enrollTermId}>Enroll</button>
              </div>
              <div className="tracks-list-simple">
                {trackStudents.map(st => (
                  <div key={st.id} className="tracks-list-row">
                    <span>{st.student_name || studentName(st.student_id)} · {termLabel(st.academic_term_id)}</span>
                    <button className="btn-icon" onClick={() => handleRemoveStudentTrack(st.id)} title="Remove from track">
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
                {!trackStudents.length && <p className="muted">No students enrolled in this track yet.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TracksPage;
